import { useQuery } from '@apollo/client';
import type { ListingsApi } from '@seek/sbiz-shared-types';
import {
  Box,
  Card,
  ContentBlock,
  Divider,
  Heading,
  Stack,
  Strong,
  Text,
  Link,
} from 'braid-design-system';
import React, { Fragment } from 'react';
import { useParams } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import { configValues } from 'src/config';
import { useEffectOnce } from 'src/hooks/useEffectOnce';
import { fireHeapEvent } from 'src/utils/analytics';

import { Error } from '../../components/Error/Error';

import Enquiry from './components/Enquiry';
import ENQUIRIES from './queries/enquiries';
import type { GraphQlUser, GraphQlUserListing } from './types/enquiries';

interface Props {
  listing: GraphQlUserListing;
}
const EnquiriesComponent = ({ listing }: Props) => {
  const enquiries = sortByDateAscending(listing.enquiries);

  useEffectOnce(() => {
    fireHeapEvent('sellerEnquiriesShow', {
      enquiryCount: enquiries.length,
    });
  });

  return (
    <Fragment>
      <Header
        title="Seller enquiries"
        description="Seller enquiries"
        url="/sell/listings/:id/enquiries"
      />
      <ContentBlock width="medium">
        <Box
          paddingX={{
            mobile: 'medium',
            tablet: 'large',
            desktop: 'large',
            wide: 'large',
          }}
          paddingBottom={{
            mobile: 'medium',
            tablet: 'xxlarge',
            desktop: 'xlarge',
            wide: 'xlarge',
          }}
        >
          <Box paddingY="medium">
            <Stack space="medium">
              <Stack space="medium">
                <Link
                  href={`${configValues.HEADER_SERVER_BASE}/business-listing/${listing.urlKey}/${listing.listingId}`}
                >
                  <Heading level="3">{listing.title}</Heading>
                </Link>
                <Heading level="4">{listing.location}</Heading>
              </Stack>

              <Divider />
              <Text>
                <Strong>
                  {enquiries.length === 1
                    ? '1 Enquiry Received'
                    : `${listing.enquiries.length} Enquiries Received`}
                </Strong>
              </Text>
              {enquiries.length > 0 ? (
                <Stack space="medium">
                  {enquiries.map((enquiry, index) => (
                    <Enquiry key={index} enquiry={enquiry} />
                  ))}
                </Stack>
              ) : (
                <Box data-testid="no-enquiries-message" paddingBottom="xxlarge">
                  <Card rounded tone="formAccent">
                    <Stack space="medium">
                      <Heading level="3">
                        {"You haven't received any enquiries yet"}
                      </Heading>
                      <Text>
                        You will be notified via email when someone enquires on
                        your listing, or you can check back here anytime to see
                        all your enquiries in one place.
                      </Text>
                    </Stack>
                  </Card>
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
      </ContentBlock>
    </Fragment>
  );
};

const Enquiries = () => {
  const { id } = useParams();
  const { loading, error, data } = useQuery(ENQUIRIES, {
    variables: { listingId: id },
  });
  if (loading) {
    return <CustomLoader />;
  }

  if (error) {
    return <Error isFullPage={true} />;
  }

  const enquiryResponse: GraphQlUser = data;
  return <EnquiriesComponent listing={enquiryResponse.user.listings[0]} />;
};

const sortByDateAscending = (enquiries: ListingsApi.ListingEnquiry[]) => {
  const enquiriesCopy = [...enquiries];
  enquiriesCopy.sort((a, b) => {
    const aDate = new Date(a.lastUpdated);
    const bDate = new Date(b.lastUpdated);
    if (aDate < bDate) {
      return 1;
    } else if (bDate < aDate) {
      return -1;
    }
    return 0;
  });
  return enquiriesCopy;
};

export default Enquiries;
